/**
 * Subscription-plan limits both the server and the browser have to agree on.
 *
 * Here rather than in `@kiao-voice/api-contract` for the same reason as
 * `transcript-format.ts`: apps/api keeps a zero runtime dependency on that
 * package, and the authoritative validator is one of the callers.
 */

/**
 * The personally purchasable paid plans: what personal checkout, coupons and
 * promotion campaigns accept as a plan code. `promotion_campaigns.plan_code`
 * and `promotion_claims.plan_code` carry a DB CHECK that
 * `apps/api/tests/unit/value_list_check_constraints.spec.ts` holds equal to
 * this list.
 */
export const PERSONAL_PAID_PLAN_CODES = ['creator', 'pro'] as const
export type PersonalPaidPlanCode = (typeof PERSONAL_PAID_PLAN_CODES)[number]

/**
 * The CLIENT-SAFE plan tier the API reports (`AuthUser.plan`) and the voice
 * limits switch on. `team` has no representation here: a team context reports
 * `creator`, the tier a seat confers.
 */
export const USER_PLANS = ['free', ...PERSONAL_PAID_PLAN_CODES] as const
export type UserPlan = (typeof USER_PLANS)[number]

/**
 * Every plan code the product sells (`subscription_plans.code`), in display
 * order. Also the staff roster's plan filter and the email-rule `plan`
 * property, where `team` means "holds a team seat".
 */
export const PLAN_CODES = [...USER_PLANS, 'team'] as const
export type PlanCode = (typeof PLAN_CODES)[number]

/**
 * Plan codes a LIVE `subscriptions` row can carry: every personally-purchasable
 * paid plan plus `team` (seat-based team subscription). Excludes `free`, which
 * isn't a subscription state at all — a subscription row never has that code.
 */
export const SUBSCRIPTION_PLAN_CODES = [...PERSONAL_PAID_PLAN_CODES, 'team'] as const
export type SubscriptionPlanCode = (typeof SUBSCRIPTION_PLAN_CODES)[number]

/**
 * Plan codes this table prices: all of `PLAN_CODES`, as a union rather than
 * `string`, and deliberately so: it exists to make `PLAN_MAX_FILE_BYTES.free`
 * and `.pro` COMPILE-CHECKED property reads.
 *
 * Typing the table as `Record<string, number>` would leave those reads
 * unverified, and the callers that name a plan directly are the two policy
 * fallbacks — so renaming a key here would silently hand them `undefined`, and
 * `file.size > undefined` is `false`, i.e. an upload guard that accepts
 * everything. Lookups by an arbitrary runtime string go through
 * `maxFileBytesForPlan`, which still takes a plain `string`.
 */
export type CappedPlanCode = PlanCode

/**
 * Per-file upload ceiling by plan code, in bytes. Mirrors the
 * `subscription_plans` seeder rows.
 *
 * The authoritative check is the server's post-parse validator; the browser
 * copies exist so a user is told "too large" before paying the upload's
 * round-trip, not so they can disagree about the number.
 */
export const PLAN_MAX_FILE_BYTES: Readonly<Record<CappedPlanCode, number>> = {
  free: 100 * 1024 * 1024,
  creator: 200 * 1024 * 1024,
  pro: 500 * 1024 * 1024,
  team: 500 * 1024 * 1024,
}

/**
 * The per-file cap for a plan code, with the unknown-plan answer supplied by
 * the caller.
 *
 * `unknownFallback` is REQUIRED because there is no single right answer and the
 * live callers genuinely disagree: the server keeps the Pro ceiling so a plan
 * migration cannot lock an account out of uploading, while the upload modal
 * keeps the Free floor so a probe that never answered cannot be read as a
 * larger allowance. A default here would silently hand one of them the other's
 * policy — which is the drift this table was pulled together to end (#636
 * case 4).
 *
 * `Object.hasOwn` rather than a plain index: an index into an object literal
 * walks the prototype, so a plan code of `constructor` or `toString` resolves
 * to a function where the signature promises a number, and the caller then
 * compares a file size against it.
 */
export function maxFileBytesForPlan(
  planCode: string,
  options: { unknownFallback: number }
): number {
  return Object.hasOwn(PLAN_MAX_FILE_BYTES, planCode)
    ? PLAN_MAX_FILE_BYTES[planCode as CappedPlanCode]
    : options.unknownFallback
}
